import React, { useMemo } from 'react';
import {
    Box,
    Divider,
    Flex,
    Icon,
    IconButton,
    Image,
    Link,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useBreakpointValue
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { formatDateWithTimezone } from 'ts/common/common';
import ResponsiveButtonGroup from 'ts/common/components/ResponsiveButtonGroup';
import { DownloadIcon, PrinterIcon } from 'ts/common/components/icons';
import ClientInvoiceDiscountCardList from 'ts/common/components/invoice/ClientInvoiceDiscountCardList';
import ClientInvoiceHeading from 'ts/common/components/invoice/ClientInvoiceHeading';
import ClientInvoiceItemCardList from 'ts/common/components/invoice/ClientInvoiceItemCardList';
import ClientInvoiceItemTableRow from 'ts/common/components/invoice/ClientInvoiceItemTableRow';
import ClientInvoiceSavedCard from 'ts/common/components/invoice/ClientInvoiceSavedCard';
import { formatCurrencyValue, isPaymentAvailable } from 'ts/common/components/invoice/helpers';
import { type IFetchHookResponse, useTranslate } from 'ts/common/hooks';
import { printPdf } from 'ts/common/utils';

const formatCityStateZip = (address: SpApi.Client.IAddress): string => {
    const cityState = [address.city, address.state].filter(Boolean).join(', ');
    const zip = address.zip ? String(address.zip) : '';

    if (cityState && zip) {
        return `${cityState} ${zip}`;
    }
    return cityState || zip;
};

interface Props {
    invoice: SpApi.Client.IInvoice;
    onRemoveSavedCard?: () => Promise<IFetchHookResponse<void>>;
}

const ClientInvoiceOverview: React.FC<Props> = ({ invoice, onRemoveSavedCard }) => {
    const isLargeScreen = useBreakpointValue({ base: false, lg: true });
    const t = useTranslate('clientInvoice');
    const navigate = useNavigate();

    const sortedItems = useMemo(
        () => [...invoice.items].sort((a, b) => a.displayOrder - b.displayOrder),
        [invoice.items]
    );

    const openPaymentPage = () => {
        navigate(`/invoice/${invoice.publicId}/payment`);
    };

    const downloadInvoice = () => {
        open(invoice.links.alternate.href, '_blank');
    };

    const printInvoice = () => {
        return printPdf(invoice.links.alternate.href);
    };

    const payInvoiceButton = (
        <Box textAlign="center" width="100%">
            <ResponsiveButtonGroup
                buttons={[
                    {
                        onClick: openPaymentPage,
                        text: t('payInvoice'),
                        variant: 'primary'
                    }
                ]}
            />
        </Box>
    );

    return (
        <Box maxW="1000px" mx="auto" as="main">
            <ClientInvoiceHeading
                invoice={invoice}
                headingRightContent={
                    <Flex gap={4} alignItems="center">
                        <IconButton
                            icon={<Icon as={PrinterIcon} boxSize="24px" />}
                            variant="ghost"
                            size="24px"
                            aria-label={t('print')}
                            onClick={printInvoice}
                            cursor="pointer"
                        />
                        <IconButton
                            icon={<Icon as={DownloadIcon} boxSize="24px" />}
                            variant="ghost"
                            size="24px"
                            aria-label={t('download')}
                            onClick={downloadInvoice}
                            cursor="pointer"
                        />
                    </Flex>
                }
            />
            <Flex flexWrap="wrap" mb={10} gap="30px" alignItems="flex-start" role="region">
                <Flex
                    gap="30px"
                    flexWrap="wrap"
                    flex="1 1 300px"
                    minW="300px"
                    as="dl"
                    aria-label={t('invoiceDetails')}
                >
                    <Box flex="0 1 auto" minW="fit-content" role="presentation">
                        <Text fontSize="14px" fontWeight="600" mb={1} as="dt">
                            {t('to')}
                        </Text>
                        <Flex flexDirection="column" as="dd" gap={0} alignItems="flex-start" ml={0}>
                            <Text fontSize="14px" fontWeight="400">
                                {invoice.contact.name}
                            </Text>
                            {invoice.contact.businessName && (
                                <Text fontSize="14px" fontWeight="400">
                                    {invoice.contact.businessName}
                                </Text>
                            )}
                            {invoice.contact.email && (
                                <Text fontSize="14px" fontWeight="400">
                                    {invoice.contact.email}
                                </Text>
                            )}
                            {invoice.contact.phone && (
                                <Text fontSize="14px" fontWeight="400">
                                    {invoice.contact.phone}
                                </Text>
                            )}
                            {invoice.contact.address && (
                                <>
                                    {invoice.contact.address.addressLine1 && (
                                        <Text fontSize="14px" fontWeight="400">
                                            {invoice.contact.address.addressLine1}
                                        </Text>
                                    )}
                                    {invoice.contact.address.addressLine2 && (
                                        <Text fontSize="14px" fontWeight="400">
                                            {invoice.contact.address.addressLine2}
                                        </Text>
                                    )}
                                    {(invoice.contact.address.city ||
                                        invoice.contact.address.state ||
                                        invoice.contact.address.zip) && (
                                        <Text fontSize="14px" fontWeight="400">
                                            {formatCityStateZip(invoice.contact.address)}
                                        </Text>
                                    )}
                                </>
                            )}
                        </Flex>
                    </Box>
                    <Box
                        flex="0 1 auto"
                        minW="fit-content"
                        role="presentation"
                        display={{ base: 'none', lg: 'block' }}
                    >
                        <Text fontSize="14px" fontWeight="600" mb={1} as="dt">
                            {t('issued')}
                        </Text>
                        <Text fontSize="14px" fontWeight="400" as="dd">
                            {formatDateWithTimezone(invoice.created, invoice.brand.timeZone)}
                        </Text>
                    </Box>
                    <Box flex="0 1 auto" minW="fit-content" role="presentation">
                        <Text fontSize="14px" fontWeight="600" mb={1} as="dt">
                            {t('dueDate')}
                        </Text>
                        <Text fontSize="14px" fontWeight="400" as="dd">
                            {formatDateWithTimezone(invoice.finalDueDate, invoice.brand.timeZone)}
                        </Text>
                    </Box>
                    {invoice.contracts.length > 0 && (
                        <Box
                            flex="0 1 auto"
                            minW="fit-content"
                            display="flex"
                            flexDirection="column"
                        >
                            <Text fontSize="14px" fontWeight="600" mb={1}>
                                {t('linkedDocuments')}
                            </Text>
                            {invoice.contracts.map((contract) => {
                                const contractHref = contract.links?.client?.href;

                                return (
                                    <Link
                                        key={contract.publicId}
                                        href={contractHref || '#'}
                                        target={contractHref ? '_blank' : undefined}
                                        textDecoration="underline"
                                        _hover={{ textDecoration: 'underline' }}
                                    >
                                        <Text
                                            fontSize="14px"
                                            fontWeight="400"
                                            color="brand.primary"
                                        >
                                            {contract.name}
                                        </Text>
                                    </Link>
                                );
                            })}
                        </Box>
                    )}
                </Flex>
                {invoice.brandTheme?.emailBrandLogo?.displayUrl?.original && (
                    <Box
                        flex="1 1 200px"
                        minW="200px"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Image
                            src={invoice.brandTheme.emailBrandLogo.displayUrl.original}
                            alt={invoice.brandTheme.logoAltText || invoice.brandName}
                            width="100%"
                            maxH="200px"
                            objectFit="contain"
                            loading="lazy"
                        />
                    </Box>
                )}
            </Flex>
            {isLargeScreen && isPaymentAvailable(invoice) && payInvoiceButton}
            <Box mb={12} mt={12} role="region" aria-label={t('invoiceContent')}>
                <Text as="h2" fontSize="20px" fontWeight="400" mt={0} mb={4} textTransform="none">
                    {t('items')}
                </Text>
                {isLargeScreen ? (
                    <Table>
                        <Thead>
                            <Tr>
                                <Th borderBottom="none">{t('item')}</Th>
                                <Th borderBottom="none" textAlign="center">
                                    {t('quantity')}
                                </Th>
                                <Th borderBottom="none" textAlign="right">
                                    {t('itemPrice')}
                                </Th>
                                <Th borderBottom="none" textAlign="right">
                                    {t('totalPrice')}
                                </Th>
                            </Tr>
                        </Thead>
                        <Tbody
                            borderColor="gray.100"
                            borderBottomStyle="solid"
                            borderBottomWidth="1px"
                        >
                            {sortedItems.map((item, index) => {
                                return (
                                    <ClientInvoiceItemTableRow
                                        key={index}
                                        description={item.description}
                                        name={item.name}
                                        quantity={item.quantity}
                                        itemPrice={formatCurrencyValue(
                                            item.itemPrice,
                                            invoice.currencyCode
                                        )}
                                        totalPrice={formatCurrencyValue(
                                            item.totalPrice,
                                            invoice.currencyCode
                                        )}
                                    />
                                );
                            })}
                            {invoice.discounts.map((discount, index) => {
                                return (
                                    <ClientInvoiceItemTableRow
                                        key={index}
                                        description={discount.description}
                                        name={discount.name}
                                        quantity="—"
                                        itemPrice={
                                            discount.discountType === 'fixed'
                                                ? t('discountOff', {
                                                      value: formatCurrencyValue(
                                                          discount.value,
                                                          invoice.currencyCode
                                                      )
                                                  })
                                                : `${discount.value}%`
                                        }
                                        totalPrice={`-${formatCurrencyValue(
                                            Number(invoice.discountTotal),
                                            invoice.currencyCode
                                        )}`}
                                    />
                                );
                            })}
                        </Tbody>
                    </Table>
                ) : (
                    <>
                        <ClientInvoiceItemCardList invoice={invoice} />
                        <ClientInvoiceDiscountCardList invoice={invoice} />
                    </>
                )}
            </Box>
            <Flex
                mb={12}
                gap="60px"
                role="region"
                aria-label={t('invoiceSummary')}
                flexDirection={{ base: 'column', lg: 'row' }}
            >
                <Box flex="1" order={{ base: 1, lg: 0 }}>
                    {invoice.notesToClient && (
                        <>
                            <Text as="h3" fontSize="18px" fontWeight="600" mb={4}>
                                {t('notes')}
                            </Text>
                            <Text fontSize="14px" lineHeight="1.6" whiteSpace="pre-wrap">
                                {invoice.notesToClient}
                            </Text>
                        </>
                    )}
                </Box>
                <Box flex="1">
                    <Box borderRadius="md" p={6} bg="gray.50">
                        <Flex justify="space-between" mb={2}>
                            <Text>{t('subtotal')}</Text>
                            <Text>
                                {formatCurrencyValue(
                                    Number(invoice.subtotal),
                                    invoice.currencyCode
                                )}
                            </Text>
                        </Flex>
                        {Number(invoice.discountTotal) > 0 && (
                            <Flex justify="space-between" mb={2}>
                                <Text>{t('discounts')}</Text>
                                <Text>
                                    -
                                    {formatCurrencyValue(
                                        Number(invoice.discountTotal),
                                        invoice.currencyCode
                                    )}
                                </Text>
                            </Flex>
                        )}
                        {invoice.salesTaxPercent > 0 && (
                            <Flex justify="space-between" mb={2}>
                                <Text>
                                    {t('salesTax')} ({invoice.salesTaxPercent}%)
                                </Text>
                                <Text>
                                    {formatCurrencyValue(
                                        Number(invoice.salesTaxTotal),
                                        invoice.currencyCode
                                    )}
                                </Text>
                            </Flex>
                        )}
                        <Divider borderColor="gray.300" />
                        <Flex justify="space-between" mb={2}>
                            <Text>{t('grandTotal')}</Text>
                            <Text>
                                {formatCurrencyValue(
                                    Number(invoice.grandTotal),
                                    invoice.currencyCode
                                )}
                            </Text>
                        </Flex>
                        {Number(invoice.paidTotal) > 0 && (
                            <Flex justify="space-between" mb={2}>
                                <Text>{t('totalPaid')}</Text>
                                <Text>
                                    {formatCurrencyValue(
                                        Number(invoice.paidTotal),
                                        invoice.currencyCode
                                    )}
                                </Text>
                            </Flex>
                        )}
                        <Divider borderColor="gray.300" />
                        <Flex justify="space-between">
                            <Text fontSize="20px" fontWeight="600">
                                {t('balanceDue')}
                            </Text>
                            <Text fontSize="16px" fontWeight="600">
                                {formatCurrencyValue(
                                    Number(invoice.dueTotal),
                                    invoice.currencyCode
                                )}
                            </Text>
                        </Flex>
                    </Box>
                </Box>
            </Flex>
            {invoice.installments.length > 0 && (
                <Box mb={12} role="region" aria-label={t('paymentSchedule')}>
                    <Text as="h2" fontSize="20px" fontWeight="400" mb={8} textTransform="none">
                        {t('paymentSchedule')}
                    </Text>
                    <Table>
                        <Thead>
                            <Tr>
                                <Th>{t('installmentNumber')}</Th>
                                <Th>{t('amount')}</Th>
                                <Th>{t('due')}</Th>
                                <Th textAlign="center">{t('status')}</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {invoice.installments.map((installment, index) => {
                                const isOverdue = new Date(installment.dueDate) < new Date();
                                const statusColor =
                                    installment.invoiceInstallmentStatus === 'paid-in-full'
                                        ? 'green.600'
                                        : installment.invoiceInstallmentStatus === 'partially-paid'
                                        ? 'orange.600'
                                        : isOverdue
                                        ? 'red.600'
                                        : 'gray.600';

                                return (
                                    <Tr key={index}>
                                        <Td fontWeight="600">{index + 1}</Td>
                                        <Td>
                                            {formatCurrencyValue(
                                                installment.amount,
                                                invoice.currencyCode
                                            )}
                                        </Td>
                                        <Td>
                                            <Text>
                                                {formatDateWithTimezone(
                                                    installment.dueDate,
                                                    invoice.brand.timeZone
                                                )}
                                            </Text>
                                            {isOverdue &&
                                                installment.invoiceInstallmentStatus !==
                                                    'paid-in-full' && (
                                                    <Text fontSize="12px" color="red.600">
                                                        {t('overdue')}
                                                    </Text>
                                                )}
                                        </Td>
                                        <Td textAlign="center">
                                            <Text color={statusColor}>
                                                {t(
                                                    `installmentStatus.${installment.invoiceInstallmentStatus}`
                                                )}
                                            </Text>
                                        </Td>
                                    </Tr>
                                );
                            })}
                        </Tbody>
                    </Table>
                    <ClientInvoiceSavedCard
                        invoice={invoice}
                        onRemoveSavedCard={onRemoveSavedCard}
                    />
                </Box>
            )}
            <Box mb={12} role="region" aria-label={t('studioInfo')}>
                <Text as="h2" fontSize="20px" fontWeight="400" mb={8} textTransform="none">
                    {t('studioInfo')}
                </Text>
                <Flex flexDirection={{ base: 'column', lg: 'row' }} gap={8}>
                    <Flex flexDirection="column">
                        <Text>{invoice.brandName}</Text>
                        {invoice.brandPhone && (
                            <Text>
                                <Link href={`tel:${invoice.brandPhone}`}>{invoice.brandPhone}</Link>
                            </Text>
                        )}
                        <Text>
                            <Link href={`mailto:${invoice.brandEmail}`}>{invoice.brandEmail}</Link>
                        </Text>
                    </Flex>
                    <Text dangerouslySetInnerHTML={{ __html: invoice.brandAddress }}></Text>
                </Flex>
            </Box>
            {!isLargeScreen && isPaymentAvailable(invoice) && payInvoiceButton}
        </Box>
    );
};

export default ClientInvoiceOverview;
